import type { ActionReference, GameInventoryReference, GameObjectReference, GameState, GameTextReference } from "@shared/types";
import type { TemplateResult } from "lit";
import { css, html, LitElement, nothing } from "lit";
import { customElement } from "lit/decorators.js";

import { Events } from "../constants";
import { getState, performAction } from "../services/routeService";

@customElement("game-canvas")
export class GameCanvas extends LitElement {
	public static styles = css`
		.game {
			height: 100%;
			display: grid;
			grid-template-columns: 1fr;
			grid-template-rows: auto auto 1fr auto;
			grid-column-gap: 0px;
			grid-row-gap: 0px;
		}

		.title {
			text-align: center;
			margin-top: 10px;
		}

		.header {
			display: flex;
			flex-direction: column;
			align-items: center;
			flex-grow: 1;
			position: relative;
			margin-top: 10px;
		}

		.header img {
			width: 100%;
			max-height: 585px;
			object-fit: contain;
			image-rendering: pixelated;
		}

		.header img:nth-child(n + 2) {
			position: absolute;
		}

		.inventory {
			position: absolute;
			top: 20px;
			right: 20px;

			background-color: #1b1b1be7;
			border-radius: 10px;
			padding: 10px 10px;

			display: flex;
			align-items: center;
			gap: 10px;
		}

		.inventory-count {
			margin-left: -5px;
		}

		.footer {
			border-radius: 10px 10px 0 0;
			background-color: #bf8555;
			border: 1px solid #e1bf92;
			margin-top: 10px;
			display: flex;
			height: 105px;
		}

		.footer .buttons {
			display: flex;
			overflow: auto;
			flex-direction: column;
			padding: 10px 10px 0 10px;
			width: 100%;
		}

		.footer .restart-button {
			background-color: #7f6ed7;
			border: 1px solid #332c57;

			display: flex !important;
			align-items: center;
			gap: 5px;
		}

		.footer .button-wrapper {
			display: flex;
			justify-content: space-between;
			overflow: auto;
			width: 100%;
		}

		.footer .button {
			background-color: #89612e;
			border: 1px solid #e1bf92;
			padding: 5px 10px;
			margin: 0 0 10px 10px;
			text-transform: uppercase;
			cursor: pointer;
			display: inline-block;
			user-select: none;
		}

		.footer .button.active,
		.footer .button:hover {
			background-color: #e1bf92;
		}
	`;

	private roomTitle?: string;
	private roomImages?: string[];
	private contentText?: GameTextReference;
	private actionButtons?: ActionReference[];
	private gameObjectButtons?: GameObjectReference[];
	private inventoryItems: GameInventoryReference[] = [];

	private selectedActionButton?: ActionReference;
	private selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

	private textAnimationFinished = false;

	public connectedCallback(): void {
		super.connectedCallback();
		window.addEventListener(Events.ANIMATION_FINISHED, this.animationFinished.bind(this));
		window.addEventListener(Events.RESTART_GAME, this.restartGame.bind(this));

		void this.refreshState();
	}

	public disconnectedCallback() {
		window.removeEventListener(Events.ANIMATION_FINISHED, this.animationFinished.bind(this));
		window.removeEventListener(Events.RESTART_GAME, this.restartGame.bind(this));
		super.disconnectedCallback();
	}

	protected render(): TemplateResult {
		return html` <div class="game">${this.renderTitle()} ${this.renderHeader()} ${this.renderContent() ?? ""} ${this.renderFooter()}</div> `;
	}

	private restartGame() {
		// Hard coding the values because in the Alias should never change and the rest should not be checked
		void this.handleClickAction({ alias: "restart-action", label: "Restart", needsObject: false, requiresAnimation: false });
	}

	private animationFinished(): void {
		this.textAnimationFinished = true;
		this.requestUpdate();
	}

	private async refreshState(): Promise<void> {
		const state: GameState = await getState();

		this.updateState(state);
	}

	private updateState(state: GameState): void {
		// Reset the component
		this.roomTitle = state.roomTitle;
		this.roomImages = state.roomImages;
		this.contentText = state.text;
		this.actionButtons = state.actions;
		this.gameObjectButtons = state.objects;
		this.inventoryItems = state.inventory;

		this.selectedActionButton = undefined;
		this.textAnimationFinished = false;
		this.selectedGameObjectButtons.clear();

		window.dispatchEvent(new CustomEvent(Events.TEXT_CHANGE, { detail: state.text }));
		this.requestUpdate();
	}

	private async handleClickAction(button: ActionReference): Promise<void> {
		if (button.needsObject) {
			this.selectedActionButton = button;
			this.selectedGameObjectButtons.clear();

			this.requestUpdate();
		} else {
			const state: any = await performAction(button.alias);

			if (state === undefined) {
				return;
			}

			this.updateState(state);
		}
	}

	private async handleClickObject(button: GameObjectReference): Promise<void> {
		if (!this.selectedActionButton) {
			return;
		}

		this.selectedGameObjectButtons.add(button);

		const state: GameState | undefined = await performAction(
			this.selectedActionButton.alias,
			[...this.selectedGameObjectButtons].map((e) => e.alias)
		);

		if (this.selectedGameObjectButtons.size >= 2) {
			this.selectedActionButton = undefined;
			this.selectedGameObjectButtons.clear();
		}

		this.requestUpdate();

		if (state === undefined) {
			return;
		}

		this.updateState(state);
	}

	private renderTitle(): TemplateResult {
		if (this.roomTitle) {
			return html`<div class="title">${this.roomTitle}</div>`;
		}

		return html`${nothing}`;
	}

	private renderHeader(): TemplateResult {
		if (this.roomImages && this.roomImages.length > 0) {
			return html`
				<div class="header">${this.roomImages?.map((url) => html`<img src="/assets/img/rooms/${url}.png" />`)} ${this.renderInventory()}</div>
			`;
		}

		return html`${nothing}`;
	}

	private renderInventory() {
		if (!this.inventoryItems.length) return nothing;

		const getEmoij = (name: string) => {
			switch (name) {
				case "key":
					return "🗝️";
				case "treasure":
					return "🪙";
				case "candle":
					return "🕯️";
				case "stick":
					return "🪤";
				case "metal-rod":
					return "🪵";
				default:
					return "📦";
			}
		};

		return html`<div class="inventory">
			${this.inventoryItems.map(
				(item) => html`
					<div class="inventory-item">
						<span>${getEmoij(item.name)}</span>
						<span class="inventory-count">${item.count}x</span>
					</div>
				`
			)}
		</div>`;
	}

	private renderContent(): TemplateResult | undefined {
		return html`<animated-text></animated-text>`;
	}

	private renderFooter(): TemplateResult {
		const restartAction = this.actionButtons?.find((action) => action.alias === "restart-action");
		const actions = this.actionButtons?.filter((action) => action.alias !== "restart-action") ?? [];

		return html`
			<div class="footer">
				<div class="buttons">
					<div class="button-wrapper">
						<div>
							${this.contentText?.type === "animated" && !this.textAnimationFinished
								? html`<a class="button" @click=${() => void window.dispatchEvent(new CustomEvent(Events.SKIP_ANIMATION))}>Skip</a>`
								: nothing}
							${actions
								.filter(
									(
										button // filter buttons that move players to next room until text animation is finished
									) =>
										this.contentText?.type === "animated" && !this.textAnimationFinished && button.requiresAnimation
											? false
											: true
								)
								.map(
									(button) =>
										html`<a
											class="button ${this.selectedActionButton === button ? "active" : ""}"
											@click=${(): void => void this.handleClickAction(button)}
											>${button.label}</a
										>`
								)}
						</div>

						<div>${restartAction ? html`<restart-button></restart-button>` : nothing}</div>
					</div>

					<div>
						${this.selectedActionButton
							? this.gameObjectButtons?.map(
									(button) =>
										html`<a
											class="button ${this.selectedGameObjectButtons.has(button) ? "active" : ""}"
											@click=${(): void => void this.handleClickObject(button)}
											>${button.name}</a
										>`
								)
							: nothing}
					</div>
				</div>
			</div>
		`;
	}
}
